import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';

type ParseState<T> =
  | { status: 'idle' }
  | { status: 'parsing' }
  | { status: 'matched'; data: T }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

interface UploadAreaProps {
  label: string;
  hint: string;
  file: File | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (file: File | null) => void;
  accept?: string;
  matchState: ParseState<unknown>;
  matchLabel?: string;
}

export default function UploadArea({
  label,
  hint,
  file,
  inputRef,
  onChange,
  accept = '.csv',
  matchState,
  matchLabel,
}: UploadAreaProps) {
  const acceptLabel = accept.replace(/\./g, '').toUpperCase();
  return (
    <Box>
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '1.5px dashed',
          borderColor: file ? 'success.main' : 'grey.300',
          borderRadius: 1,
          p: 1.5,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: file ? 'success.50' : 'grey.50',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': {
            borderColor: file ? 'success.dark' : 'primary.main',
            bgcolor: file ? 'success.50' : 'primary.50',
          },
        }}
      >
        {file ? (
          <CheckCircleIcon fontSize="small" color="success" />
        ) : (
          <UploadFileIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={500} color={file ? 'success.dark' : 'text.primary'}>
              {label}
            </Typography>
            <Tooltip title={hint} arrow>
              <InfoOutlinedIcon
                sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default' }}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Stack>
          <Typography
            variant="caption"
            color={file ? 'success.main' : 'text.secondary'}
            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {file ? file.name : `Click to upload ${acceptLabel}`}
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        />
      </Box>
      {matchState.status === 'parsing' && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5, px: 0.5 }}>
          <CircularProgress size={10} />
          <Typography variant="caption" color="text.secondary">
            Matching...
          </Typography>
        </Stack>
      )}
      {matchState.status === 'matched' && (
        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          Matched Property: {matchLabel}
        </Typography>
      )}
      {matchState.status === 'not-found' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          Not found in file
        </Typography>
      )}
      {matchState.status === 'error' && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
          {matchState.message}
        </Typography>
      )}
    </Box>
  );
}
